import { Box, alpha, useTheme } from '@mui/material';
import { useThemeMode } from '../theme/ThemeModeContext';
import { conmemoracionDeHoy } from '../theme/conmemoracion';
import { ConLaExplicacion } from './TooltipConmemoracion';

interface BoosterMarkProps {
  /** Alto del monograma en px (el wordmark escala en proporción). */
  height?: number;
  /** Mostrar el wordmark "BOOSTER" al lado del monograma. */
  showWordmark?: boolean;
  /** Pulso suave del glow del monograma (ej. en la barra de navegación). */
  pulse?: boolean;
  /** Logo "vivo": glow pulse + chevrons del boost animándose hacia arriba. */
  animate?: boolean;
  /** White-label: logo propio del tenant (reemplaza el monograma + wordmark). */
  logoUrl?: string;
  /** White-label: nombre de marca del tenant (reemplaza "BOOSTER" en el wordmark). */
  name?: string;
  /**
   * Pisa el acento del monograma (el vidrio y su borde). Sin esto sale el `primary.main`
   * de la marca activa, que es lo correcto DENTRO del CRM. La web pública de Booster lo
   * usa para que el logo no sea otra mancha verde sobre la nebulosa: ahí el verde quedó
   * reservado para el logotipo en sí, y la caja pasó a vidrio frío. (20/08/2026)
   */
  accentColor?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}

const DISPLAY = "'Orbitron', 'Inter', sans-serif";

// Glyph "boost + casa": doble chevron (techo, impulso hacia arriba) + dos
// pilares laterales (paredes) → identidad inmobiliaria para Booster.
export function BoostGlyph({ size = 24, color = '#FFFFFF', strokeWidth = 2.4, animated = false }: { size?: number; color?: string; strokeWidth?: number; animated?: boolean }) {
  const common = { stroke: color, strokeWidth, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
      {animated && (
        <style>{`
          @keyframes bstBoost { 0%,100% { transform: translateY(0); opacity: .8 } 50% { transform: translateY(-1.4px); opacity: 1 } }
          .bst-r1 { animation: bstBoost 1.7s ease-in-out infinite; }
          .bst-r2 { animation: bstBoost 1.7s ease-in-out infinite; animation-delay: .22s; }
          @media (prefers-reduced-motion: reduce) { .bst-r1, .bst-r2 { animation: none } }
        `}</style>
      )}
      {/* techo superior (chevron que impulsa) */}
      <path className={animated ? 'bst-r1' : undefined} d="M5 11 L12 5.5 L19 11" {...common} />
      {/* techo inferior */}
      <path className={animated ? 'bst-r2' : undefined} d="M5 15 L12 9.5 L19 15" {...common} />
      {/* pilares / paredes */}
      <path d="M5 15 L5 19.5"  {...common} />
      <path d="M19 15 L19 19.5" {...common} />
    </svg>
  );
}

// Lockup de marca Booster: boost mark (caja emerald sólida + doble chevron) +
// wordmark Orbitron. El acento sale de `primary.main` (marca activa del CRM).
// Drop-in del logo en topbar/sidebar. Usar showWordmark={false} para modo compacto.
export function BoosterMark({ height = 32, showWordmark = true, pulse = false, animate = false, logoUrl, name, accentColor, onClick, style }: BoosterMarkProps) {
  const theme  = useTheme();
  const { mode } = useThemeMode();
  const accent = accentColor ?? theme.palette.primary.main;
  const radius = Math.round(height * 0.3);
  const glow   = pulse || animate;

  // White-label: logo propio → reemplaza el lockup entero.
  if (logoUrl) {
    // El logo dorado de Imperium (monocromo) se vuelve NEGRO en modo claro (filter: brightness(0)
    // = misma silueta, sin sumar asset). Solo el logo Imperium: un logo a color de otro tenant no se toca.
    const esLogoImperium = /imperium-logo/i.test(logoUrl);
    const monoInLight = mode === 'light' && esLogoImperium;
    /**
     * El 25 de Agosto el logo de Imperium va celeste con la banderita.
     *
     * ⛔ SOLO el logo de Imperium. Este componente corre en todas las inmobiliarias de la
     * red —y la red es multi-país—: teñir de celeste el logo de un tenant que no es
     * uruguayo sería pintarle la marca por una fecha que no es suya.
     *
     * Gana sobre `monoInLight`: en modo claro el logo se pinta de negro, y hoy el celeste
     * es justamente el punto.
     */
    const conmemoracion = esLogoImperium ? conmemoracionDeHoy() : null;
    const logo = (
      <Box
        component="img"
        src={logoUrl}
        alt={name ?? 'Logo'}
        onClick={conmemoracion ? undefined : onClick}
        style={conmemoracion ? undefined : style}
        sx={{
          height, maxHeight: height, maxWidth: 180, objectFit: 'contain', display: 'block',
          cursor: onClick ? 'pointer' : 'default', userSelect: 'none',
          // En modo claro la barra es blanca y el celeste normal se pierde contra ella.
          filter: conmemoracion
            ? (mode === 'light' ? conmemoracion.filtroSobreClaro : conmemoracion.filtro)
            : (monoInLight ? 'brightness(0)' : undefined),
        }}
      />
    );
    if (!conmemoracion) return logo;
    return (
      <ConLaExplicacion conmemoracion={conmemoracion}>
      <Box
        onClick={onClick}
        style={style}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0, cursor: onClick ? 'pointer' : 'default' }}
      >
        {logo}
        <Box component="span" aria-hidden sx={{ fontSize: Math.round(height * 0.6), lineHeight: 1, ml: '-20px', transform: 'translateY(-12px)' }}>
          {conmemoracion.bandera}
        </Box>
      </Box>
      </ConLaExplicacion>
    );
  }

  return (
    <Box
      onClick={onClick}
      style={style}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.1,
        userSelect: 'none',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'opacity 0.15s ease',
        '&:hover': onClick ? { opacity: 0.85 } : {},
      }}
    >
      <Box
        sx={{
          width: height, height, flexShrink: 0,
          borderRadius: `${radius}px`,
          display: 'grid', placeItems: 'center',
          // Mismo look que el FAB de Gustavo: verde semi-transparente (glass) + borde.
          background: alpha(accent, 0.22),
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(accent, 0.55)}`,
          boxShadow: `0 6px 16px ${alpha(accent, 0.3)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.15)}`,
          ...(glow && {
            '@keyframes bstMarkPulse': {
              '0%, 100%': { transform: 'translateY(0) scale(1)',       boxShadow: `0 6px 14px ${alpha(accent, 0.22)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.15)}` },
              '50%':      { transform: 'translateY(-3px) scale(1.04)', boxShadow: `0 12px 30px ${alpha(accent, 0.7)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.2)}` },
            },
            animation: 'bstMarkPulse 2.2s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }),
        }}
      >
        <BoostGlyph size={Math.round(height * 0.6)} animated={animate} />
      </Box>

      {showWordmark && (
        <Box
          component="span"
          style={{ fontFamily: DISPLAY }}
          sx={{ fontWeight: 800, fontSize: Math.round(height * 0.52), letterSpacing: name ? '0.04em' : '0.15em', color: 'text.primary', lineHeight: 1, textTransform: name ? 'none' : 'uppercase', whiteSpace: 'nowrap' }}
        >
          {name || 'BOOSTER'}
        </Box>
      )}
    </Box>
  );
}
